import * as React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { siteMessages } from 'src/features/sites/utils/messages';
import { PERMISSION } from 'src/features/auth/types';
import { useDeleteMembershipType } from 'src/features/membershipType/api/useDeleteMembershipType';
import { MEMBERSHIP_TYPE_RESOURCE, useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { LIST_MEMBERSHIP_TYPES_RESOURCE } from 'src/features/membershipType/api/useListMembershipTypes';
import { usePatchMembershipType } from 'src/features/membershipType/api/usePatchMembershipType';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import DashboardGeneralBlock from 'src/features/stay/components/dashboard/DashboardGeneralBlock';
import ActivateMembershipType from 'src/features/membershipType/components/forms/ActivateMembershipType';
import DeleteMembershipType from 'src/features/membershipType/components/forms/DeleteMembershipType';
import UpdateMembershipTypeFormModal from 'src/features/membershipType/components/forms/UpdateMembershipTypeFormModal';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import {
    DURATION_UNIT_PLURAL_TRANSLATION_MAP,
    membershipTypeBadgeOptions,
} from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import AllowedTimesTable from 'src/features/membershipType/components/table/AllowedTimesTable';
import RestrictedGatewaysTable from 'src/features/membershipType/components/table/RestrictedGatewaysTable';
import ZoneRulesTable from 'src/features/membershipType/components/table/ZoneRulesTable';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { LOCALE } from 'src/providers/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlock, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import BackButton from 'src/features/navigation/components/BackButton';

const MembershipTypeDashboard = () => {
    const { formatMessage, locale } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const navigate = useNavigate();
    const manageMembershipType = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIP_TYPES]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);

    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();
    const { data: membershipTemplate } = useGetMembershipType({
        parkingId,
        membershipTypeId,
    });

    const templateName = membershipTemplate?.translations?.find((template) => template.language === locale)?.name;
    const templateActiveStatus = !!membershipTemplate?.is_active;
    const limitSpaces = !!membershipTemplate?.has_allowed_spaces_count;
    const allowCustomDuration = membershipTemplate?.limit_duration === true && membershipTemplate?.duration === null;
    const membershipType = membershipTemplate?.category;
    const membershipTypeBadgeOption = membershipType && membershipTypeBadgeOptions[membershipType];
    const duration = () => {
        if (!membershipTemplate?.duration && !membershipTemplate?.duration_unit) return '-';
        return formatMessage(DURATION_UNIT_PLURAL_TRANSLATION_MAP[membershipTemplate?.duration_unit], {
            value: membershipTemplate?.duration,
        });
    };

    const limitToRoles = membershipTemplate?.limit_to_roles?.map((role) => {
        return role.name;
    });

    React.useEffect(() => {
        if (!membershipTemplate) return;
        setTitle(formatMessage(membershipTypeMessages.membershipTemplateDashboard));
        return removeTitle;
    }, [removeTitle, setTitle, templateName, formatMessage, membershipTemplate]);

    const { mutate: deleteTemplate } = useDeleteMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPES_RESOURCE] });
            void navigate(`/${PATH.ADMINISTRATION}/membership-templates`);
            toast({
                title: formatMessage(membershipTypeMessages.deleteTemplateSuccess),
                description: formatMessage(membershipTypeMessages.deleteTemplateSuccessDescription, {
                    entry: membershipTemplate?.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: activateTemplate } = usePatchMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            toast({
                title: formatMessage(membershipTypeMessages.activatedTemplateSuccess),
                description: formatMessage(membershipTypeMessages.activatedTemplateSuccessDescription, {
                    entry: membershipTemplate?.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    if (!membershipTemplate) return null;

    const dashboardBlockData = {
        section1: [
            {
                title: membershipTypeMessages.nameBg,
                value:
                    membershipTemplate?.translations?.find((template) => template.language === LOCALE.BG)?.name || '-',
            },
            {
                title: membershipTypeMessages.nameEn,
                value:
                    membershipTemplate?.translations?.find((template) => template.language === LOCALE.EN)?.name || '-',
            },
            {
                title: commonMessages.type,
                value: membershipTypeBadgeOption ? (
                    <Badge theme={membershipTypeBadgeOption?.theme || 'gray'}>
                        <FontAwesomeIcon icon={membershipTypeBadgeOption?.icon as IconProp} />
                        {formatMessage(membershipTypeBadgeOption?.label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: siteMessages.site,
                value: (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/site/${membershipTemplate?.site.id}`}
                        linkTitle={
                            membershipTemplate?.site?.translations?.find((site) => site.language === locale)?.name
                        }
                        hasPermissions={accessSites}
                    />
                ),
            },
        ],
        section2: [
            {
                title: membershipTypeMessages.priced,
                value: <BooleanBadge booleanField={!!membershipTemplate?.has_pricing_plan} />,
            },
            {
                title: pricingPlanMessages.pricingPlan,
                value: (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/pricing-plan/${membershipTemplate.pricing_plan?.id}`}
                        linkTitle={
                            membershipTemplate?.pricing_plan?.translations?.find((plan) => plan.language === locale)
                                ?.name
                        }
                        hasPermissions={accessPricingPlans}
                    />
                ),
            },
            {
                title: membershipTypeMessages.pricedPerSpace,
                value: <BooleanBadge booleanField={!!membershipTemplate?.apply_pricing_per_space} />,
            },
            {
                title: membershipTypeMessages.autoRenew,
                value: <BooleanBadge booleanField={!!membershipTemplate?.auto_renew} />,
            },
        ],
        section3: [
            {
                title: membershipTypeMessages.limitDuration,
                value: <BooleanBadge booleanField={!!membershipTemplate?.limit_duration} />,
            },
            {
                title: membershipTypeMessages.allowCustomDuration,
                value: <BooleanBadge booleanField={allowCustomDuration} />,
            },
            {
                title: membershipTypeMessages.duration,
                value: duration(),
            },
        ],
        section4: [
            {
                title: membershipTypeMessages.limitSpaces,
                value: <BooleanBadge booleanField={limitSpaces} />,
            },
            {
                title: membershipTypeMessages.maxAllowedSpaces,
                value: membershipTemplate?.max_allowed_spaces_count ?? '-',
            },
            {
                title: membershipTypeMessages.customSpaceCount,
                value: <BooleanBadge booleanField={!!membershipTemplate?.allow_custom_space_count_definition} />,
            },
            {
                title: membershipTypeMessages.extraVisitors,
                value: <BooleanBadge booleanField={!!membershipTemplate?.allow_extra_visitors} />,
            },
        ],
        section5: [
            {
                title: membershipTypeMessages.allowAccessOverCapacityLimit,
                value: <BooleanBadge booleanField={!!membershipTemplate?.allow_access_over_capacity_limit} />,
            },
            {
                title: membershipTypeMessages.contributeToAvailableSpaces,
                value: <BooleanBadge booleanField={!!membershipTemplate?.contribute_to_available_spaces} />,
            },
            {
                title: membershipTypeMessages.allowAccessAsClientOutsideRestrictions,
                value: (
                    <BooleanBadge booleanField={!!membershipTemplate?.allow_access_as_client_outside_restrictions} />
                ),
            },
            {
                title: membershipTypeMessages.limitToRoles,
                value: limitToRoles && limitToRoles?.length > 0 ? limitToRoles.join(', ') : '-',
            },
        ],
    };

    return (
        <>
            <BackButton />

            <SectionBlock>
                <div className="flex flex-row items-end justify-between p-0">
                    <span className="flex flex-row items-center">
                        <SectionBlockTitle className="mr-2">{templateName}</SectionBlockTitle>
                        <Badge theme={templateActiveStatus ? 'green' : 'red'}>
                            <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} className="opacity-50" />
                            {templateActiveStatus
                                ? formatMessage(membershipTypeMessages.activeTemplate)
                                : formatMessage(membershipTypeMessages.inactiveTemplate)}
                        </Badge>
                    </span>

                    {manageMembershipType && (
                        <div className="flex flex-row gap-2">
                            <ActivateMembershipType
                                selectedTemplate={membershipTemplate}
                                activateTemplate={activateTemplate}
                            >
                                <Button size="mdIcon" disabled={!!membershipTemplate?.is_active}>
                                    <div className="block md:hidden">
                                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                                    </div>
                                    <span className="hidden md:block">
                                        {formatMessage(membershipTypeMessages.activateTemplate)}
                                    </span>
                                </Button>
                            </ActivateMembershipType>

                            <UpdateMembershipTypeFormModal selectedMembershipTemplate={membershipTemplate}>
                                <Button size="mdIcon" variant="outline">
                                    <div className="block md:hidden">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </div>
                                    <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                </Button>
                            </UpdateMembershipTypeFormModal>

                            <DeleteMembershipType selectedTemplate={membershipTemplate} deleteTemplate={deleteTemplate}>
                                <Button size="mdIcon" variant="destructiveLight">
                                    <div className="block md:hidden">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </div>
                                    <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                </Button>
                            </DeleteMembershipType>
                        </div>
                    )}
                </div>

                <GeneralBlock asChild>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={4} />
                </GeneralBlock>
            </SectionBlock>

            <ZoneRulesTable membershipTypeId={membershipTypeId} />
            <AllowedTimesTable membershipTypeId={membershipTypeId} />
            <RestrictedGatewaysTable membershipTypeId={membershipTypeId} />
        </>
    );
};

export default MembershipTypeDashboard;
